import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

export function useAdminData() {
	const [customers, setCustomers] = useState([])
	const [deliveryStats, setDeliveryStats] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	// Fetch all customers with their sensor configs
	const fetchCustomers = useCallback(async () => {
		try {
			const { data, error } = await supabase
				.from('customers')
				.select('*, sensor_configs(*)')
				.order('name')

			if (error) throw error
			setCustomers(data || [])
			return data
		} catch (err) {
			console.error('Error fetching customers:', err)
			setError(err.message)
			throw err
		}
	}, [])

	// Fetch delivery statistics
	const fetchDeliveryStats = useCallback(async () => {
		try {
			const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()

			// Recent measurements (24h)
			const { count: recentMeasurements, error: measError } = await supabase
				.from('aggregated_measurements')
				.select('id', { count: 'exact', head: true })
				.gte('timestamp', yesterday)

			if (measError) throw measError

			// Pending deliveries (24h, not delivered)
			const { count: pendingDeliveries, error: pendError } = await supabase
				.from('aggregated_measurements')
				.select('id', { count: 'exact', head: true })
				.gte('timestamp', yesterday)
				.is('delivered_at', null)

			if (pendError) throw pendError

			// Failed deliveries (24h)
			const { count: failedDeliveries, error: failError } = await supabase
				.from('webhook_deliveries')
				.select('id', { count: 'exact', head: true })
				.eq('status', 'failed')
				.gte('attempted_at', yesterday)

			if (failError) throw failError

			// Recent delivery activity
			const { data: recentDeliveries, error: delError } = await supabase
				.from('webhook_deliveries')
				.select('*, customer:customers(name)')
				.order('attempted_at', { ascending: false })
				.limit(10)

			if (delError) throw delError

			const stats = {
				recentMeasurements: recentMeasurements || 0,
				pendingDeliveries: pendingDeliveries || 0,
				failedDeliveries: failedDeliveries || 0,
				recentDeliveries: recentDeliveries || []
			}

			setDeliveryStats(stats)
			return stats
		} catch (err) {
			console.error('Error fetching delivery stats:', err)
			setError(err.message)
			throw err
		}
	}, [])

	// Create a new customer
	const createCustomer = useCallback(async (customerData) => {
		try {
			const { data, error } = await supabase
				.from('customers')
				.insert({
					name: customerData.name,
					webhook_url: customerData.webhook_url || '',
					webhook_api_key: customerData.webhook_api_key || '',
					webhook_enabled: (customerData.webhook_url?.trim() ? customerData.webhook_enabled : false) || false,
					show_branding: customerData.show_branding ?? true,
					custom_logo: customerData.custom_logo?.trim() || null
				})
				.select()
				.single()

			if (error) throw error

			// If sensors provided, create them
			if (customerData.sensors && customerData.sensors.length > 0) {
			const sensorInserts = customerData.sensors.map(sensor => ({
				customer_id: data.id,
				sensor_id: sensor.sensor_id,
				sensor_location: sensor.sensor_location || '',
				delivery_enabled: false,
				realtime_enabled: false,
				show_alkalinity: true,
				show_conductivity: false,
				show_temperature: false,
				show_ph: false,
				api_fetch_enabled: false
			}))

				const { error: sensorError } = await supabase
					.from('sensor_configs')
					.insert(sensorInserts)

				if (sensorError) throw sensorError
			}

			await fetchCustomers()
			return data
		} catch (err) {
			console.error('Error creating customer:', err)
			throw err
		}
	}, [fetchCustomers])

	// Update an existing customer
	const updateCustomer = useCallback(async (customerId, customerData) => {
		try {
			const { data, error } = await supabase
				.from('customers')
				.update({
					name: customerData.name,
					webhook_url: customerData.webhook_url || null,
					webhook_api_key: customerData.webhook_api_key || '',
					webhook_enabled: customerData.webhook_url?.trim() ? !!customerData.webhook_enabled : false,
					show_branding: customerData.show_branding ?? true,
					custom_logo: customerData.custom_logo?.trim() || null
				})
				.eq('id', customerId)
				.select()

			if (error) throw error

			await fetchCustomers()
			return data
		} catch (err) {
			console.error('Error updating customer:', err)
			throw err
		}
	}, [fetchCustomers])

	// Delete a customer
	const deleteCustomer = useCallback(async (customerId) => {
		try {
			// Delete webhook deliveries first (they reference aggregated_measurements)
			const { error: webhookError } = await supabase
				.from('webhook_deliveries')
				.delete()
				.eq('customer_id', customerId)

			if (webhookError) throw webhookError

			// Delete aggregated measurements
			const { error: measurementsError } = await supabase
				.from('aggregated_measurements')
				.delete()
				.eq('customer_id', customerId)

			if (measurementsError) throw measurementsError

			// Delete sensor configs
			const { error: sensorError } = await supabase
				.from('sensor_configs')
				.delete()
				.eq('customer_id', customerId)

			if (sensorError) throw sensorError

			// Delete customer
			const { error } = await supabase
				.from('customers')
				.delete()
				.eq('id', customerId)

			if (error) throw error

			await fetchCustomers()
		} catch (err) {
			console.error('Error deleting customer:', err)
			throw err
		}
	}, [fetchCustomers])

	// Create a sensor config
	const createSensor = useCallback(async (customerId, sensorData) => {
		try {
			// Get the max ID to avoid sequence conflicts
			const { data: maxData } = await supabase
				.from('sensor_configs')
				.select('id')
				.order('id', { ascending: false })
				.limit(1)
				.maybeSingle()

			const nextId = maxData ? maxData.id + 1 : 1

			const { data, error } = await supabase
				.from('sensor_configs')
				.insert({
					id: nextId,
					customer_id: customerId,
					sensor_id: sensorData.sensor_id,
					sensor_location: sensorData.sensor_location || '',
				delivery_enabled: false,
				realtime_enabled: false,
				show_alkalinity: true,
				show_conductivity: false,
				show_temperature: false,
				show_ph: false,
				api_fetch_enabled: false
			})
			.select()

			if (error) throw error

			await fetchCustomers()
			return data
		} catch (err) {
			console.error('Error creating sensor:', err)
			throw err
		}
	}, [fetchCustomers])

	// Update a sensor config
	const updateSensor = useCallback(async (sensorConfigId, sensorData) => {
		try {
			const { data, error } = await supabase
				.from('sensor_configs')
				.update({
					sensor_id: sensorData.sensor_id,
					sensor_location: sensorData.sensor_location
				})
				.eq('id', sensorConfigId)
				.select()

			if (error) throw error

			// Update local state without re-fetching to prevent rearrangement
			setCustomers(prev => prev.map(customer => ({
				...customer,
				sensor_configs: customer.sensor_configs?.map(sensor =>
					sensor.id === sensorConfigId
						? { ...sensor, sensor_id: sensorData.sensor_id, sensor_location: sensorData.sensor_location }
						: sensor
				)
			})))
			
			return data
		} catch (err) {
			console.error('Error updating sensor:', err)
			throw err
		}
	}, [])

	// Delete a sensor config
	const deleteSensor = useCallback(async (sensorConfigId) => {
		try {
			const { error } = await supabase
				.from('sensor_configs')
				.delete()
				.eq('id', sensorConfigId)

			if (error) throw error

			await fetchCustomers()
		} catch (err) {
			console.error('Error deleting sensor:', err)
			throw err
		}
	}, [fetchCustomers])

	// Toggle sensor delivery
	const toggleSensorDelivery = useCallback(async (customerId, sensorId) => {
		try {
			// First fetch current state
			const { data: currentData, error: fetchError } = await supabase
				.from('sensor_configs')
				.select('delivery_enabled')
				.eq('customer_id', customerId)
				.eq('sensor_id', sensorId)
				.single()

			if (fetchError) throw fetchError

			// Then toggle
			const { error } = await supabase
				.from('sensor_configs')
				.update({ delivery_enabled: !currentData.delivery_enabled })
				.eq('customer_id', customerId)
				.eq('sensor_id', sensorId)

			if (error) throw error

			await fetchCustomers()
		} catch (err) {
			console.error('Error toggling sensor delivery:', err)
			throw err
		}
	}, [fetchCustomers])

	// Toggle customer webhook
	const toggleCustomerWebhook = useCallback(async (customerId) => {
		try {
			// Fetch current state
			const { data: currentData, error: fetchError } = await supabase
				.from('customers')
				.select('webhook_enabled')
				.eq('id', customerId)
				.single()

			if (fetchError) throw fetchError

			// Toggle
			const { error } = await supabase
				.from('customers')
				.update({ webhook_enabled: !currentData.webhook_enabled })
				.eq('id', customerId)

			if (error) throw error

			await fetchCustomers()
		} catch (err) {
			console.error('Error toggling webhook:', err)
			throw err
		}
	}, [fetchCustomers])

	// Update sensor visibility flags
	const updateSensorVisibility = useCallback(async (sensorConfigId, visibilityData) => {
		try {
			const { error } = await supabase
				.from('sensor_configs')
				.update({
					show_alkalinity: visibilityData.show_alkalinity,
					show_conductivity: visibilityData.show_conductivity,
					show_temperature: visibilityData.show_temperature,
					show_ph: visibilityData.show_ph
				})
				.eq('id', sensorConfigId)

			if (error) throw error

			// Update local state without re-fetching to prevent rearrangement
			setCustomers(prev => prev.map(customer => ({
				...customer,
				sensor_configs: customer.sensor_configs?.map(sensor =>
					sensor.id === sensorConfigId
						? { ...sensor, ...visibilityData }
						: sensor
				)
			})))
		} catch (err) {
			console.error('Error updating sensor visibility:', err)
			throw err
		}
	}, [])

	// Toggle sensor realtime enabled
	const toggleSensorRealtimeEnabled = useCallback(async (sensorConfigId) => {
		try {
			// Fetch current state
			const { data: currentData, error: fetchError } = await supabase
				.from('sensor_configs')
				.select('realtime_enabled')
				.eq('id', sensorConfigId)
				.single()

			if (fetchError) throw fetchError

			const newValue = !currentData.realtime_enabled

			// Toggle
			const { error } = await supabase
				.from('sensor_configs')
				.update({ realtime_enabled: newValue })
				.eq('id', sensorConfigId)

			if (error) throw error

			// Update local state without re-fetching to prevent rearrangement
			setCustomers(prev => prev.map(customer => ({
				...customer,
				sensor_configs: customer.sensor_configs?.map(sensor =>
					sensor.id === sensorConfigId
						? { ...sensor, realtime_enabled: newValue }
						: sensor
				)
			})))
		} catch (err) {
			console.error('Error toggling sensor realtime:', err)
			throw err
		}
	}, [])

	// Toggle sensor API enabled
	const toggleSensorApiEnabled = useCallback(async (sensorConfigId) => {
		try {
			// Fetch current state
			const { data: currentData, error: fetchError } = await supabase
				.from('sensor_configs')
				.select('api_fetch_enabled')
				.eq('id', sensorConfigId)
				.single()

			if (fetchError) throw fetchError

			const newValue = !currentData.api_fetch_enabled

			// Toggle
			const { error } = await supabase
				.from('sensor_configs')
				.update({ api_fetch_enabled: newValue })
				.eq('id', sensorConfigId)

			if (error) throw error

			// Update local state without re-fetching to prevent rearrangement
			setCustomers(prev => prev.map(customer => ({
				...customer,
				sensor_configs: customer.sensor_configs?.map(sensor =>
					sensor.id === sensorConfigId
						? { ...sensor, api_fetch_enabled: newValue }
						: sensor
				)
			})))
		} catch (err) {
			console.error('Error toggling sensor API access:', err)
			throw err
		}
	}, [])

	// Transfer a sensor to a different customer (future ownership only)
	const transferSensor = useCallback(async (sensorConfigId, targetCustomerId) => {
		try {
			const { error } = await supabase
				.from('sensor_configs')
				.update({ customer_id: targetCustomerId })
				.eq('id', sensorConfigId)

			if (error) throw error

			await fetchCustomers()
		} catch (err) {
			console.error('Error transferring sensor:', err)
			throw err
		}
	}, [fetchCustomers])

	// Reassign the currently logged-in admin to exactly one customer
	const assignCurrentUserToCustomer = useCallback(async (userId, customerId, createdBy) => {
		try {
			const { error: deleteError } = await supabase
				.from('user_customers')
				.delete()
				.eq('user_id', userId)

			if (deleteError) throw deleteError

			const { data, error } = await supabase
				.from('user_customers')
				.insert({
					user_id: userId,
					customer_id: customerId,
					created_by: createdBy || 'admin_self_assignment',
				})
				.select()
				.single()

			if (error) throw error

			return data
		} catch (err) {
			console.error('Error assigning current user to customer:', err)
			throw err
		}
	}, [])

	// Initial data load
	useEffect(() => {
		const loadData = async () => {
			try {
				setLoading(true)
				await Promise.all([fetchCustomers(), fetchDeliveryStats()])
			} catch (err) {
				console.error('Error loading admin data:', err)
			} finally {
				setLoading(false)
			}
		}

		loadData()
	}, [fetchCustomers, fetchDeliveryStats])

	return {
		customers,
		deliveryStats,
		loading,
		error,
		operations: {
			fetchCustomers,
			fetchDeliveryStats,
			createCustomer,
			updateCustomer,
			deleteCustomer,
			createSensor,
			updateSensor,
			deleteSensor,
			toggleSensorDelivery,
			toggleCustomerWebhook,
			updateSensorVisibility,
			toggleSensorRealtimeEnabled,
			toggleSensorApiEnabled,
			transferSensor,
			assignCurrentUserToCustomer,
		}
	}
}
